/**
 * The HTTP status and the backend's machine-readable `code` from an error thrown
 * by apiRequest (utils/apiClient ApiError: `status`, plus the parsed JSON body
 * in `details`, e.g. `{ code: 'PROFESSIONAL_ACCOUNT_REQUIRED' }`).
 *
 * Duck-typed rather than `instanceof ApiError`, so it reads any `{ status,
 * details }` shape. Anything else reads as `{ status: 0, code: null }`, which is
 * what apiRequest itself uses for a network failure.
 */
export interface ApiErrorInfo {
  status: number;
  code: string | null;
}

export function apiErrorInfo(error: unknown): ApiErrorInfo {
  if (!error || typeof error !== 'object') return { status: 0, code: null };
  const { status, details } = error as { status?: unknown; details?: unknown };
  const code =
    details && typeof details === 'object' && typeof (details as { code?: unknown }).code === 'string'
      ? (details as { code: string }).code
      : null;
  return { status: typeof status === 'number' ? status : 0, code };
}

/**
 * requireAuth refuses a suspended account with 403 ACCOUNT_SUSPENDED on every
 * signed-in call. The site already words that case (the sign-in page uses it),
 * so callers show this existing key rather than a "try again" error.
 */
export const ACCOUNT_SUSPENDED_MESSAGE_KEY = 'common:auth.accountSuspended';

export function isAccountSuspended(error: unknown): boolean {
  return apiErrorInfo(error).code === 'ACCOUNT_SUSPENDED';
}
